import {GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, LoadSpec, lookup, managed, XH} from '@xh/hoist/core';
import {action, bindable, observable} from '@xh/hoist/mobx';
import {GridOptions} from 'ag-grid-community';
import {priceCol} from '../../../../cmp/PositionColumns';
import {wait} from '@xh/hoist/promise';
import {TopTrading, TopTradingTypeEnum} from '../../../../data/DataTypes';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {Timer} from '@xh/hoist/utils/async';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';
import {fmtPercent, fmtPrice} from '@xh/hoist/format';
import {secExchangeBadge} from '../../../../cmp/secBadges/SecBadges';
import {tickerPopup} from '../../../portfolio/impl/positions/TickerPanel';

export class TopTradingWidgetModel extends HoistModel {
    @observable accessor type: TopTradingTypeEnum;
    @bindable accessor count = -1;
    @bindable accessor exchange: string = null;
    isVisible: boolean;
    @managed gridModel = this.createGridModel();
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    @managed private timer = Timer.create({interval: 3000, runFn: () => this.loadAsync()});

    override onLinked() {
        this.markPersist('type', {dashViewModel: this.dashViewModel});
        this.markPersist('count', {dashViewModel: this.dashViewModel});

        this.dashViewModel.extraMenuItems.push({
            icon: Icon.gear(),
            text: 'Configure',
            items: Object.values(TopTradingTypeEnum).map(type => ({
                text: type,
                key: type,
                actionFn: () => this.setType(type)
            }))
        });

        wait().then(async () => {
            await this.gridModel.whenReadyAsync();
            this.addReaction({
                track: () => this.count,
                run: () => this.updatePageSize()
            });
        });

        this.addReaction({
            track: () => this.exchange,
            run: () => this.setFilter()
        });
    }

    get agOptions(): GridOptions {
        return {
            pagination: true,
            suppressPaginationPanel: true,
            paginationPageSize: this.count === -1 ? 0 : this.count,
            paginationAutoPageSize: this.count === -1
        };
    }

    @action
    private setType(type: TopTradingTypeEnum) {
        this.type = type;
        this.dashViewModel.title = type;
        this.gridModel.clear();
        this.loadAsync();
    }

    private updatePageSize() {
        if (this.count === -1) {
            if (!this.gridModel.isReady) return;
            this.gridModel.agApi.setGridOption('paginationAutoPageSize', true);
        } else {
            if (!this.gridModel.isReady) return;
            this.gridModel.agApi.setGridOption('paginationAutoPageSize', false);
            this.gridModel.agApi.setGridOption('paginationPageSize', this.count);
        }
    }

    private setFilter() {
        if (!this.exchange) this.gridModel.store.clearFilter();
        else this.gridModel.store.setFilter({field: 'exchange', op: '=', value: this.exchange});
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const {type} = this;
        if (!type) return;

        if (!this.isVisible) {
            return;
        }

        const data: TopTrading[] = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/economics/topTrading/${type}`,
            method: 'GET',
            ...getDefaultOptions()
        });

        this.gridModel.loadData(data);
    }

    private createGridModel(): GridModel {
        return new GridModel({
            store: {
                idSpec: 'ticker'
            },
            sortBy: 'changePct|desc',
            contextMenu: null,
            colDefaults: {
                movable: false,
                resizable: false,
                sortable: false
            },
            columns: [
                {
                    field: 'ticker',
                    width: 60,
                    renderer: (v, {record}) =>
                        tickerPopup({
                            // @ts-ignore - partial position used intentionally for popup context
                            position: {ticker: v}
                        })
                },
                {...priceCol, field: 'price'},
                {field: 'name', hidden: true},
                {
                    field: 'change',
                    width: 100,
                    align: 'right',
                    renderer: v => fmtPrice(v, {colorSpec: true})
                },
                {
                    field: 'changePct',
                    headerName: 'Change %',
                    width: 100,
                    align: 'right',
                    sortable: true,
                    renderer: v => fmtPercent(v, {colorSpec: true})
                },
                {field: 'exchange', width: 100, renderer: v => secExchangeBadge(v)}
            ]
        });
    }
}
